import { faro } from '@grafana/faro-react';
import Button from '@platform-ui-common/components/Button/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import EmailLink from '@platform-ui-common/components/Link/EmailLink.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useSnackbar, { SnackbarKey } from '@platform-ui-common/hooks/useSnackbar/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { ApplicantLayout } from '@platform-ui/application/components/index.js';
import { BEAM_SUPPORT_EMAIL } from '@platform-ui/utilities/constants.js';
import { ErrorInfo, ReactNode, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom-v5-compat';
import { useErrorBoundary, withErrorBoundary } from 'react-use-error-boundary';

function ErrorButton(): JSX.Element {
  const history = useHistory();
  return <Button onClick={(): void => history.goBack()}>GO BACK</Button>;
}

const ErrorBoundary = withErrorBoundary(({ children }: { children: ReactNode }): JSX.Element => {
  const [error, resetError] = useErrorBoundary((error: Error, errorInfo: ErrorInfo) => {
    if (faro?.api?.pushError) {
      const errorWithComponentStack = new Error(error.message);
      errorWithComponentStack.name = `React ErrorBoundary ${error.name}`;
      errorWithComponentStack.stack = errorInfo.componentStack;

      faro.api.pushError(errorWithComponentStack);
    }
  });
  const { showSnackbar, removeSnackbar } = useSnackbar();

  const hasRenderedOnce = useRef(false);
  const location = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset this when the location changes, even though that dependency isn't used in the hook
  useEffect(() => {
    if (!error) return;
    if (!hasRenderedOnce.current) hasRenderedOnce.current = true;
    // The hook does have correct return types set up, I'm not quite sure why it's only
    // not registering when running tests...
    else (resetError as () => void)();
  }, [location.key, error, resetError]);

  if (error) {
    const debugId = faro?.api?.getSession()?.id;
    const debugMessage = debugId ? (
      <>
        &nbsp;and provide the following debug id: <br /> <br />
        <Button
          icon="contentCopy"
          iconPosition="end"
          className="normal-case"
          variant="text"
          tooltip="Click to copy Debug ID to clipboard"
          shrink={true}
          onClick={() => {
            navigator.clipboard.writeText(debugId);
            showSnackbar(
              'Debug id copied to clipboard. Please include it in your email to support.',
              (key: SnackbarKey) => (
                <Button
                  variant="text"
                  textColor={Color.Link}
                  onClick={(): void => removeSnackbar(key)}
                >
                  Dismiss
                </Button>
              ),
            );
          }}
        >
          <strong>{debugId}</strong>&nbsp;
        </Button>
      </>
    ) : (
      <></>
    );

    return (
      <ApplicantLayout>
        <FormContainer title="We're sorry. Something went wrong." buttons={<ErrorButton />}>
          <div className="lg:w-1/2 text-center">
            <Typography variant="body">
              Unfortunately, this page is currently down. Please try again in a little bit.
            </Typography>
            <br />
            <Typography variant="body">
              If the problem persists, please contact us at{' '}
              <EmailLink>{BEAM_SUPPORT_EMAIL}</EmailLink>
              {debugMessage}
            </Typography>
          </div>
        </FormContainer>
      </ApplicantLayout>
    );
  }
  return <>{children}</>;
});

export default ErrorBoundary;
